import { Code } from '@connectrpc/connect';
import * as Sentry from '@sentry/react';

import { redirectToLoginPage } from '@/domains/authentication/login';
import { clearSessionToken } from '@/domains/authentication/session';
import { getApiErrorDetails, isExpectedApiError } from '@/lib/apiError';
import { toastQueue } from '@/lib/toast';

import i18n from '../i18n/i18n';

// Global TanStack Query error handler: toasts the error with the backend request ID (so a user
// report can be matched to backend logs) and reports unexpected failures to Sentry.
export function handleApiError(error: unknown, customMessage?: string) {
	const { connectError, code, codeName, requestId, errorCode } =
		getApiErrorDetails(error);

	if (code === Code.Canceled || code === Code.NotFound) {
		return;
	}

	// A missing or expired session is not an error to report: the user just has to sign in.
	if (code === Code.Unauthenticated) {
		clearSessionToken();
		redirectToLoginPage();
		return;
	}

	if (!isExpectedApiError(code)) {
		Sentry.captureException(connectError, {
			tags: {
				request_id: requestId,
				connect_code: codeName,
				error_code: errorCode,
			},
		});
	}

	const title =
		customMessage ||
		connectError.rawMessage ||
		i18n.t('common.api.genericError');
	toastQueue.add(
		{ type: 'error', title, reference: requestId },
		{ timeout: requestId ? 10000 : 5000 },
	);
}
